import { AdSource, LogoType, useGetProfileQuery } from '@kijiji/generated/graphql-types'
import WebsiteGlobeIcon from '@kijiji/icons/src/icons/WebsiteGlobe'
import { useTranslation } from 'next-i18next'
import { type FC } from 'react'
import { useTheme } from 'styled-components'

import { ShowAtOrLarger } from '@/components/shared/breakpoint'
import { VIP_LOCATION_VARIANT, VipLocation } from '@/components/vip/shared/vip-location/VipLocation'
import { VipAutoVerify } from '@/components/vip/vip-auto-verify/VipAutoVerify'
import { InlineIconLink } from '@/components/vip/vip-seller-profile/InlineIconLink'
import {
  VipSideProfileLogo,
  VipSideProfileLogoContainer,
  VipSideProfileOverviewSection,
  VipSideProfileStickyContainer,
  VipSideProfileTopContainer,
} from '@/components/vip/vip-seller-profile/styled'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { R2S } from '@/features/listing/components/reply-to-seller/R2S'
import { RequestViewing } from '@/features/listing/components/request-viewing/RequestViewing'
import { ProfilePhoneNumber } from '@/features/profile/components/phone-number/ProfilePhoneNumber'
import { SellerOverviewCard } from '@/features/profile/components/SellerOverviewCard'
import { getCommercialProfileFromData } from '@/features/profile/utils/getCommercialProfileFromData'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { type VipQueryListing } from '@/types/listings'
import { Flex } from '@/ui/atoms/flex'
import { Spacing } from '@/ui/atoms/spacing'
import { generateVIPPhoneTrackingLabel } from '@/utils/vip/generateVIPPhoneTrackingLabel'

type VipSideProfileSectionProps = {
  listing: VipQueryListing
  isListingFromDealer: boolean
  /** Professional doesn't include only dealerships, but also KMB and commercial profiles */
  isProfessionalSeller: boolean
  isSellerVip: boolean
  /**
   * Callback to be passed down to the R2SForm component
   */
  callbackForR2S?: () => void
}

export const VipSideProfileSection: FC<VipSideProfileSectionProps> = ({
  isListingFromDealer,
  isProfessionalSeller,
  isSellerVip,
  listing,
  callbackForR2S,
}) => {
  const { t } = useTranslation(TRANSLATION_KEYS.PROFILE)
  const { spacing } = useTheme()

  const { data } = useGetProfileQuery({
    variables: {
      profileId: listing.posterInfo.posterId,
      size: LogoType.ViewAd,
    },
  })

  const handleViewWebsiteClick = () => {
    if (listing.adSource === AdSource.Organic) {
      trackEvent({ action: GA_EVENT.R2SExternalBegin, label: 'btn=WebsiteURL;' })
    }
  }

  const commercialData = getCommercialProfileFromData(data?.profile, listing)
  const logoUrl = commercialData?.logoUrl
  const websiteUrl = commercialData?.websiteUrl ?? listing?.posterInfo.websiteUrl

  return (
    <>
      {logoUrl ? (
        <VipSideProfileTopContainer>
          <VipSideProfileLogoContainer>
            <VipSideProfileLogo alt="" src={logoUrl} />
          </VipSideProfileLogoContainer>
        </VipSideProfileTopContainer>
      ) : null}

      <VipSideProfileStickyContainer hasTopContainer={!!logoUrl}>
        <VipSideProfileOverviewSection flexDirection="column" gap={spacing.default}>
          <ShowAtOrLarger breakpoint="large">
            <Flex flexDirection="column" gap={spacing.default}>
              <SellerOverviewCard
                googleReviewsGaEvent={GA_EVENT.GoogleReviewClickR2S}
                hideAvatar={isListingFromDealer}
                hideForSaleBy
                listing={listing}
                logoType={LogoType.ViewAd}
                userId={listing.posterInfo.posterId}
              />

              <Flex flexDirection="column" gap={spacing.defaultSmall}>
                <VipLocation
                  location={listing.location}
                  showSolidIcon
                  variant={VIP_LOCATION_VARIANT.LINK}
                />

                {/* Website URL */}
                {websiteUrl ? (
                  <InlineIconLink
                    Icon={WebsiteGlobeIcon}
                    href={websiteUrl}
                    isExternalLink
                    label={t('profile.metrics.website')}
                    onClick={handleViewWebsiteClick}
                  />
                ) : null}
              </Flex>

              <RequestViewing source="VIP" listing={listing} />

              {listing.posterInfo.phoneNumber ? (
                <ProfilePhoneNumber
                  listingId={listing.id}
                  profileId={listing.posterInfo.posterId}
                  trackingLabel={generateVIPPhoneTrackingLabel(isListingFromDealer, 'top')}
                />
              ) : null}
            </Flex>
          </ShowAtOrLarger>

          <VipAutoVerify listing={listing} isDealerListing={isListingFromDealer} />
        </VipSideProfileOverviewSection>

        <Spacing pTop={spacing.default}>
          <R2S
            categoryId={listing.categoryId}
            disableForm={isSellerVip}
            isProfessionalSeller={isProfessionalSeller}
            listingId={listing.id}
            locationId={listing.location.id}
            callbackForR2S={callbackForR2S}
          />
        </Spacing>
      </VipSideProfileStickyContainer>
    </>
  )
}
